"use client";

import React, { DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { MdArrowUpward } from 'react-icons/md';
import { chatUISliceActions, chatUiSliceSelectors } from "@/store/features/chat/chatUiSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { usePathname } from "next/navigation";
import { uploadFiles } from "@/app/components/file-upload-component";
import { CustomIconClip } from "@/components/custom-icons/CustomIconClip";
import { AgentDropdown } from "@/app/components/agent-dropdown";
import { projectFilesApiActions } from "@/store/features/file/fileApiSlice";
import { ChatImage, ChatImageProps2, imageUploadStatus } from "@/app/components/chat-image";
import { ChatImageProps } from "@/services/models/chat-image";
import { usePromptBarTheme } from "./hooks/usePromptBarTheme";
import { shadeHex } from "./utils/color";
import { useToast } from '@/app/components/toast/toast';
import {
  UnifiedPromptBarProps,
  SubmitParams,
} from "./types";

export default function UnifiedPromptBar({
  onSubmit,
  features = {},
  config = {},
  theme: themeMode = 'auto'
}: UnifiedPromptBarProps) {
  const dispatch = useAppDispatch();
  const currentMessage = useSelector(chatUiSliceSelectors.selectCurrentMessage);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isSending = useSelector(chatUiSliceSelectors.isLoading);
  const selectedAgent = useAppSelector(state => state.chatUI.selectedAgent);
  const images = useAppSelector(state => state.chatUI.promptBarImages);
  const { toast } = useToast();

  // File upload mutations (only used if fileUpload is enabled)
  const [uploadFile] = projectFilesApiActions.useUploadFileMutation();
  const [putUploadTempFile] = projectFilesApiActions.usePutUploadTempFileMutation();
  const [postProcessTempFile] = projectFilesApiActions.usePostProcessTempFileMutation();
  const [postNewTempFile] = projectFilesApiActions.usePostNewTempFileMutation();

  const pathname = usePathname();
  const theme = usePromptBarTheme(themeMode);

  // Local state
  const [draft, setDraft] = useState(currentMessage ?? '');
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Configuration with defaults
  const {
    placeholder = "Ask EON",
    defaultAgent = null,
    lockSend = false,
    maxTextareaHeight = 200,
  } = config;

  // Features with defaults
  const {
    agentSelection = false,
    fileUpload = false,
    imagePaste = false,
    dragAndDrop = false,
  } = features;

  // Determine uid for file uploads
  const uid = pathname.includes("projectManager")
    ? "f643b008-864f-44e7-b0f4-cd71f1f9b07f"
    : useAppSelector(state => state.chatUI.chat?.collection_id);

  // Get existing files for duplicate checking
  const { data: existingFiles } = projectFilesApiActions.useGetProjectFilesQuery(uid || '', {
    skip: !uid || !fileUpload
  });

  const sendLocked = lockSend || isSending;

  // Set default agent if provided
  const setSelectedAgent = React.useMemo(() => (agent: string) => {
    dispatch(chatUISliceActions.setSelectedAgent(agent));
  }, [dispatch]);

  useEffect(() => {
    if (defaultAgent && !selectedAgent && agentSelection) {
      setSelectedAgent(defaultAgent);
    }
  }, [defaultAgent, selectedAgent, setSelectedAgent, agentSelection]);

  // Auto-resize the textarea to fit content up to maxTextareaHeight
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, maxTextareaHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [draft, maxTextareaHeight]);

  // Sync draft with Redux state when uid changes
  useEffect(() => {
    setDraft(currentMessage ?? '');
  }, [uid, currentMessage]);

  // Refocus the textarea once sending finishes
  useEffect(() => {
    if (!isSending) {
      textareaRef.current?.focus();
    }
  }, [isSending]);

  const onChangeDraft = (val: string) => {
    setDraft(val);
    dispatch(chatUISliceActions.setCurrentMessage(val));
  };

  // Check if we can send
  const canSend = !sendLocked &&
    !!draft.trim() &&
    (!fileUpload || !images.some(x => x.status !== imageUploadStatus.COMPLETED)) &&
    (!agentSelection || !!selectedAgent);

  const handleSendMessage = async () => {
    const trimmedMessage = draft.trim();
    if (!canSend) return;

    // Check agent requirement
    if (agentSelection && !selectedAgent) {
      console.warn('Agent required but not selected');
      return;
    }

    try {
      // Clear optimistically
      setDraft('');
      dispatch(chatUISliceActions.setCurrentMessage(''));

      // Reset textarea height
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) {
          ta.style.height = 'auto';
          const newHeight = Math.min(ta.scrollHeight, maxTextareaHeight);
          ta.style.height = `${newHeight}px`;
          ta.focus();
        }
      });

      // Build submit params
      const params: SubmitParams = { message: trimmedMessage };
      if (agentSelection && selectedAgent) params.agent = selectedAgent;
      if (fileUpload) params.files = images.filter(x => !!x.fileId);

      await onSubmit(params);

      // Clear images after successful send
      dispatch(chatUISliceActions.removeAllImages());
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    await handleSendMessage();
  };

  // Enter to send, Shift+Enter for newline
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!canSend) return;
      await handleSendMessage();
    }
  };

  const getNextName = (name: string, names: string[]) => {
    let i = 1;
    let newName = name;
    do {
      newName = `${name}-${i}`;
      i++;
    } while (names.includes(newName));
    return newName;
  };

  // Image upload helper
  const uploadImage = useCallback(async (image: ChatImageProps) => {
    if (!fileUpload) return;

    dispatch(chatUISliceActions.setImage({ ...image }));
    const file = image.image;
    image.status = imageUploadStatus.INITIAL;

    try {
      const postNewTempFileRes = await postNewTempFile({ size: file.size, name: file.name });
      const js = postNewTempFileRes.data;
      if (!js) throw new Error("Failed to create temp file");
      await putUploadTempFile({ file: file, image: image, fileTempResponse: js });
      await postProcessTempFile({ image: image, fileTempResponse: js });
    } catch (e) {
      console.error("Upload error:", e);
      image.status = imageUploadStatus.ERROR;
      image.error = e instanceof Error ? e.message : "Upload failed";
      dispatch(chatUISliceActions.setImage({ ...image }));
    }
  }, [fileUpload, dispatch, postNewTempFile, putUploadTempFile, postProcessTempFile]);

  // File upload handlers (only active if fileUpload is enabled)
  const handleFileSelect = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!fileUpload || isSending) {
      e.target.value = '';
      return;
    }
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    e.target.value = '';

    if (files.length === 0) return;

    const items = files.map((x) => {
      const name = getNextName(`${x?.name ?? "No name"}`, images.map(x => x.name));
      return {
        image: x,
        name: name,
        onClose: () => {
          dispatch(chatUISliceActions.removeImage(name));
        },
        status: imageUploadStatus.INITIAL,
        progress: 0
      };
    });

    await Promise.all(items.map(async x => await uploadImage(x)));
  }, [uploadImage, dispatch, fileUpload, isSending, images]);

  const handleDrop = useCallback(async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!dragAndDrop || isSending) return;
    setIsDragging(false);

    // Filter to only accept image files
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));

    if (droppedFiles.length === 0) return;

    const items = droppedFiles.map((x) => {
      const name = getNextName(`${x?.name ?? "No name"}`, images.map(x => x.name));
      return {
        image: x,
        name: name,
        onClose: () => {
          dispatch(chatUISliceActions.removeImage(name));
        },
        status: imageUploadStatus.INITIAL,
        progress: 0
      };
    });

    await Promise.all(items.map(async x => await uploadImage(x)));
  }, [uploadImage, dispatch, dragAndDrop, isSending, images]);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragAndDrop || isSending) return;
    setIsDragging(true);
  }, [dragAndDrop, isSending]);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragAndDrop) return;
    setIsDragging(false);
  }, [dragAndDrop]);

  // Image paste handler (only active if imagePaste is enabled)
  const onPaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!imagePaste) return;

    const clipboardItems = e.clipboardData.items;
    const items = [].slice.call(clipboardItems)
      .map((x: DataTransferItem) => x)
      .filter(item => /^image\//.test(item.type))
      .map(x => x.getAsFile())
      .filter(x => !!x)
      .map((x) => {
        const name = getNextName(`${x?.name ?? "No name"}`, images.map(x => x.name));
        return {
          image: x,
          name: name,
          onClose: () => {
            dispatch(chatUISliceActions.removeImage(name));
          },
          status: imageUploadStatus.INITIAL,
          progress: 0
        };
      });

    if (items.length <= 0) return;

    e.preventDefault();
    e.stopPropagation();
    await Promise.all(items.map(async x => await uploadImage(x)));
  }, [dispatch, images, imagePaste, uploadImage]);

  // Button styling based on theme
  const getButtonStyle = (): React.CSSProperties => {
    const disabledButton = !canSend;

    if (disabledButton) {
      return {
        backgroundColor: '#d1d5db',
        cursor: 'not-allowed',
        opacity: 0.9
      };
    }

    if (theme.type === 'widget' && theme.widgetConfig) {
      const primary = theme.widgetConfig.palette?.primary ?? '#14B89B';
      const primaryDark = theme.widgetConfig.palette?.primaryDark ?? shadeHex(primary, -12);
      return {
        background: `linear-gradient(180deg, ${primary} 0%, ${primaryDark} 100%)`,
        opacity: isButtonHovered ? 0.9 : 1,
        transition: 'opacity 0.2s ease-in-out',
      };
    }

    // Main chat theme
    if (theme.mainTheme) {
      const accentColor = theme.mainTheme.isDefault ? '#F8C131' : `#${theme.mainTheme.tertiary_color}`;
      return {
        backgroundColor: accentColor,
        opacity: isButtonHovered ? 0.85 : 1,
        transition: 'opacity 0.2s ease-in-out',
      };
    }

    // Fallback
    return {
      backgroundColor: '#F8C131',
      opacity: isButtonHovered ? 0.85 : 1,
    };
  };

  // Prepare images for display
  const preparedImages: ChatImageProps2[] = React.useMemo(() =>
    images.map((x, _) => ({
      onClose: x.onClose,
      imageUrl: URL.createObjectURL(x.image),
      status: {
        error: x.error,
        status: x.status,
        progress: x.progress
      }
    })),
    [images]
  );

  // Drag handlers object (only if dragAndDrop is enabled)
  const dragHandlers = dragAndDrop ? {
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  } : {};

  // Widget style (simplified, inline form)
  const isWidgetStyle = theme.type === 'widget';

  if (isWidgetStyle) {
    // Widget version: compact inline form with file upload support
    return (
      <div>
        {/* Image preview area */}
        {fileUpload && preparedImages.length > 0 && (
          <div className="flex items-end gap-2 mb-2">
            {preparedImages.map((image, i) => (
              <ChatImage key={i} {...image} />
            ))}
          </div>
        )}
        <form
          onSubmit={handleSubmit}
          className={`w-full bg-white border ${isDragging ? 'border-dashed border-blue-500' : 'border-gray-200'
            } rounded-3xl p-3 focus-within:outline-none focus-within:ring-0 focus-within:border-gray-200 focus-within:shadow-none`}
          {...dragHandlers}
        >
          <div className="flex items-center">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => onChangeDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={imagePaste ? onPaste : undefined}
              placeholder={placeholder}
              rows={1}
              disabled={isSending}
              className="flex-grow border-0 bg-transparent text-md text-gray-700
                         outline-none focus:outline-none focus-visible:outline-none
                         focus:ring-0 focus-visible:ring-0
                         focus:border-transparent focus-visible:border-transparent
                         placeholder-gray-500 resize-none min-h-8 overflow-hidden
                         leading-relaxed disabled:opacity-50 py-1 appearance-none"
              aria-label="Message input"
            />

            {/* File upload button */}
            {fileUpload && (
              <>
                <button
                  type="button"
                  className={`p-1.5 rounded-full transition-colors duration-200 flex-shrink-0 ml-2 ${isSending ? 'opacity-50 cursor-not-allowed bg-transparent' : 'bg-transparent hover:bg-gray-100'
                    }`}
                  aria-label="Upload file"
                  onClick={() => !isSending && document.getElementById('widgetFileInput')?.click()}
                  disabled={isSending}
                >
                  <CustomIconClip />
                </button>
                <input
                  id="widgetFileInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={isSending}
                />
              </>
            )}

            <button
              type="submit"
              disabled={!canSend}
              className="p-1.5 rounded-full transition-opacity duration-200 flex-shrink-0 ml-2 text-white shadow"
              style={getButtonStyle()}
              onMouseEnter={() => setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
              aria-label="Send message"
              title="Send"
            >
              <MdArrowUpward className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Main chat version: full form with agent dropdown and file uploads
  return (
    <div>
      {/* Image preview area */}
      {fileUpload && preparedImages.length > 0 && (
        <div className="flex items-end gap-2 m-1">
          {preparedImages.map((image, i) => (
            <ChatImage key={i} {...image} />
          ))}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className={`w-full pointer-events-auto bg-white border ${isDragging ? 'border-dashed border-blue-500 border-1.5 opacity-65' : 'border-gray-200'
          } rounded-3xl p-4 focus-within:outline-none ${isDragging ? 'focus:outline-none' : ''
          } flex flex-col gap-4`}
        {...dragHandlers}
      >
        <textarea
          onPaste={imagePaste ? onPaste : undefined}
          ref={textareaRef}
          value={draft}
          onChange={(e) => onChangeDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={false}
          className="w-full border-0 bg-transparent text-md text-gray-700 outline-none focus:outline-none
                     focus:ring-0 focus-visible:ring-0 focus:border-1 placeholder-gray-700 resize-none min-h-10
                     leading-relaxed disabled:opacity-50"
          aria-label="Message input"
          {...(dragAndDrop && uid ? {
            onDragLeave: handleDragLeave,
            onDragOver: handleDragOver,
            onDrop: handleDrop
          } : {})}
        />

        <div className="flex justify-between items-center">
          {/* Agent dropdown (conditional) */}
          {agentSelection ? (
            <AgentDropdown
              selectedAgent={selectedAgent ?? ""}
              onAgentSelect={setSelectedAgent}
              direction={uid ? "up" : "down"}
              initialAgent={defaultAgent}
            />
          ) : (
            <div /> // Empty div to maintain layout
          )}

          <div className="flex items-center gap-2">
            {/* File upload button (conditional) */}
            {fileUpload && (
              <>
                <button
                  type="button"
                  className={`p-2 rounded-full transition-colors duration-200 flex-shrink-0 ${isSending ? 'opacity-50 cursor-not-allowed bg-transparent' : 'bg-transparent hover:bg-gray-100'
                    }`}
                  aria-label="Upload file"
                  onClick={() => !isSending && document.getElementById('fileInput')?.click()}
                  disabled={isSending}
                >
                  <CustomIconClip />
                </button>
                <input
                  id="fileInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={isSending}
                />
              </>
            )}

            {/* Send button */}
            <button
              type="submit"
              disabled={!canSend}
              aria-disabled={!canSend}
              className="p-2 rounded-full transition-colors duration-200 flex-shrink-0"
              style={getButtonStyle()}
              onMouseEnter={() => setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
              aria-label="Send message"
            >
              <MdArrowUpward className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

